import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { fehlerText, getCameraSegments, getShots } from "../../lib/api";

const COV_STYLE = {
  joint: "bg-emerald-500/80 hover:bg-emerald-400",
  seg_only: "bg-amber-400/80 hover:bg-amber-300",
  none: "bg-rose-500/10 border border-rose-400/70 hover:bg-rose-400/30",
};

// 🆕 Kamera-Segmentierungs-Pass: stabile Kamera-Abschnitte (zwischen
// Schwenks/Zooms) als klickbare Abdeckungs-Leiste unter der Timeline.
// grün = joint-Anker · gelb = nur Segment-Label · leer/umrandet = Anker
// fehlt → Klick springt zur Abschnittsmitte.
// variant="inline": nur Leiste + kompakte Meta-Zeile, ohne Card-Rahmen —
// wird exakt in Slider-Breite eingebettet (Klickposition = Zeitposition).
export const CameraCoverageBar = ({ sequenceId, duration, window: win, onSeek, variant = "card" }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // 🆕 Shot-Grenzen (PySceneDetect-Cuts) als Marker in der Leiste
  const [shots, setShots] = useState([]);

  useEffect(() => {
    if (!sequenceId) return;
    let alive = true;
    getShots(sequenceId)
      .then((d) => { if (alive) setShots(d?.shots || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [sequenceId]);

  // 🆕 04.09. · „Danach Seite neu laden" abgeschafft: Ein 409 der
  // Segmentierungs-Route heißt „720p-Proxy wird noch erzeugt" (der
  // Text trägt den MB-Stand). Die Leiste fragt dann alle 6 s selbst
  // nach — die MB-Zahl zählt live hoch, und sobald der Proxy fertig
  // ist, erscheinen die Abschnitte von allein (die Aufnahme-Kette
  // segmentiert automatisch). Kein F5 mehr.
  const [wartetAufProxy, setWartetAufProxy] = useState(false);
  // Verlauf der MB-Stände aus den 409-Meldungen [{t, mb}] — daraus
  // rechnet die Anzeige Prozent + Restzeit (siehe unten).
  const [proxyVerlauf, setProxyVerlauf] = useState([]);

  const load = useCallback(async (recompute = false) => {
    if (!sequenceId) return;
    setBusy(true);
    try {
      setData(await getCameraSegments(sequenceId, recompute));
      setError(null);
      setWartetAufProxy(false);
      setProxyVerlauf([]);
    } catch (e) {
      const txt = fehlerText(e) || "Segmentierung fehlgeschlagen";
      setError(txt);
      const istProxy409 = e?.response?.status === 409;
      setWartetAufProxy(istProxy409);
      if (istProxy409) {
        const m = /([\d.,]+)\s*MB/.exec(txt);
        if (m) {
          const mb = parseFloat(m[1].replace(",", "."));
          if (Number.isFinite(mb)) {
            setProxyVerlauf((v) => [...v.slice(-9), { t: Date.now(), mb }]);
          }
        }
      }
    } finally {
      setBusy(false);
    }
  }, [sequenceId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!wartetAufProxy) return undefined;
    const t = setInterval(() => load(), 6000);
    return () => clearInterval(t);
  }, [wartetAufProxy, load]);
  useEffect(() => {
    const h = () => {
      load();
      // 🆕 Track-/Klassen-Meta mitaktualisieren (Kamera-Track-Zeile)
      getShots(sequenceId).then((d) => setShots(d?.shots || [])).catch(() => {});
    };
    globalThis.addEventListener("anchors-changed", h);
    return () => globalThis.removeEventListener("anchors-changed", h);
  }, [load, sequenceId]);

  const winStart = win?.start ?? 0;
  const winEnd = win?.end ?? duration;
  const span = Math.max(0.001, winEnd - winStart);
  const pct = (t) => Math.max(0, Math.min(100, ((t - winStart) / span) * 100));

  const segs = (data?.segments || []).filter(
    (s) => s.t_end > winStart && s.t_start < winEnd,
  );
  const nUncov = data?.n_uncovered ?? 0;

  const strip = (
    <div
      className="relative h-2.5 rounded-sm bg-secondary/50 overflow-hidden"
      title="Dunkle Lücken = Kamera schwenkt/zoomt (kein stabiler Anker möglich)"
    >
      {busy && !data && <div className="absolute inset-0 bg-secondary animate-pulse" />}
      {/* 🆕 Shot-Grenzen (Cuts): vertikale cyan Marker */}
      {shots.slice(1)
        .filter((s) => s.start_s > winStart && s.start_s < winEnd)
        .map((s, i) => (
          <span
            key={`cut-${i}`}
            data-testid={`coverage-cut-${i}`}
            className="absolute top-0 h-full w-[2px] bg-cyan-400/80 z-10 pointer-events-none"
            style={{ left: `${pct(s.start_s)}%` }}
            title={`Schnitt → Shot ${s.shot_id} @ ${s.start_s.toFixed(2)} s`}
          />
        ))}
      {segs.map((s, i) => (
        <button
          key={`${s.t_start}-${i}`}
          type="button"
          data-testid={`coverage-segment-${i}`}
          data-coverage={s.coverage}
          onClick={() => onSeek(
            Math.min(Math.max((s.t_start + s.t_end) / 2, winStart), winEnd),
          )}
          title={`${s.t_start.toFixed(1)}–${s.t_end.toFixed(1)} s · ${
            s.coverage === "joint"
              ? `${s.n_joint} Anker gesetzt`
              : s.coverage === "seg_only"
                ? "nur Segment-Label — Homographie-Anker fehlt"
                : "kein Anker — klicken zum Springen"
          }`}
          className={`absolute top-0 h-full cursor-pointer transition-colors ${COV_STYLE[s.coverage]}`}
          style={{
            left: `${pct(s.t_start)}%`,
            width: `${Math.max(0.6, pct(s.t_end) - pct(s.t_start))}%`,
          }}
        />
      ))}
    </div>
  );

  // 🆕 2026-08 · Kamera-Track-Zeile (Phase 5): getrackte Spur-Segmente
  // (teal) + Referenz-Anker (weiße Ticks) — Lücken fallen sofort auf.
  const trackShots = shots.filter(
    (s) => s.track && s.end_s > winStart && s.start_s < winEnd,
  );
  const trackStrip = trackShots.length > 0 ? (
    <div
      data-testid="camera-track-bar"
      className="relative h-1.5 rounded-sm bg-secondary/40 overflow-hidden"
      title="Kamera-Track (Optical Flow): teal = getrackte Homographie-Spur · weiße Ticks = Referenz-Anker · Lücken fallen auf Interpolation zurück"
    >
      {trackShots.flatMap((s) => {
        const raw = (s.track.segments && s.track.segments.length)
          ? s.track.segments
          : (s.track.t_start != null ? [[s.track.t_start, s.track.t_end]] : []);
        // 🆕 Qualitäts-Ampel: v3-Segmente sind {t0,t1,grade,med_inliers};
        // v2-Arrays [[a,b]] bleiben neutral teal.
        const segs = raw.map((g) => Array.isArray(g)
          ? { t0: g[0], t1: g[1], grade: null, inl: null }
          : { t0: g.t0, t1: g.t1, grade: g.grade, inl: g.med_inliers });
        const gradeCls = {
          "grün": "bg-emerald-400/85",
          "gelb": "bg-amber-400/85",
          "rot": "bg-rose-500/85",
        };
        return segs.map((g, i) => (
          <span
            key={`tseg-${s.shot_id}-${i}`}
            data-testid={`track-seg-${s.shot_id}-${i}`}
            data-grade={g.grade || "none"}
            className={`absolute top-0 h-full ${gradeCls[g.grade] || "bg-teal-400/80"}`}
            style={{
              left: `${pct(g.t0)}%`,
              width: `${Math.max(0.4, pct(g.t1) - pct(g.t0))}%`,
            }}
            title={`Shot ${s.shot_id}: Kamera-Spur ${Number(g.t0).toFixed(1)}–${Number(g.t1).toFixed(1)} s${g.grade ? ` · Qualität ${g.grade} (${g.inl} RANSAC-Inlier median)` : ""}`}
          />
        ));
      })}
      {trackShots.flatMap((s) =>
        (s.track.ref_ts || [])
          .filter((r) => r > winStart && r < winEnd)
          .map((r, i) => (
            <span
              key={`tref-${s.shot_id}-${i}`}
              className="absolute top-0 h-full w-[2px] bg-white/70 z-10"
              style={{ left: `${pct(r)}%` }}
              title={`Referenz-Anker @ ${Number(r).toFixed(1)} s`}
            />
          )),
      )}
    </div>
  ) : null;

  const uncoveredLabel = data && (
    <span
      data-testid="coverage-uncovered-count"
      className={nUncov > 0 ? "text-rose-300 font-bold" : "text-emerald-300"}
    >
      {nUncov > 0
        ? (variant === "inline"
          ? `${nUncov} ohne Anker`
          : `${nUncov} Abschnitt${nUncov === 1 ? "" : "e"} ohne Anker`)
        : (variant === "inline" ? "alle abgedeckt" : "alle Abschnitte abgedeckt")}
    </span>
  );

  const refreshBtn = (
    <button
      data-testid="coverage-recompute-btn"
      type="button"
      onClick={() => load(true)}
      disabled={busy}
      className="text-muted-foreground hover:text-primary disabled:opacity-40"
      title="Segmentierung neu berechnen"
    >
      <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
    </button>
  );

  const legend = (
    <>
      <span>
        <span className="inline-block w-2 h-2 bg-emerald-500/80 rounded-[2px] mr-1 align-middle" />
        Anker gesetzt
      </span>
      <span>
        <span className="inline-block w-2 h-2 bg-amber-400/80 rounded-[2px] mr-1 align-middle" />
        nur Segment-Label
      </span>
      <span>
        <span className="inline-block w-2 h-2 border border-rose-400/70 rounded-[2px] mr-1 align-middle" />
        Anker fehlt (klicken)
      </span>
    </>
  );

  // 2026-08-31 · Die Proxy-Absage nennt jetzt den echten Zustand und ist
  // damit zwei bis drei Saetze lang. In der Legendenzeile (9 px, flex,
  // whitespace-nowrap overflow-hidden) waere sie schlicht abgeschnitten
  // worden - also eine eigene, umbrechende Zeile.
  // 🆕 04.09. · Prozent + Restzeit statt „dauert einige Minuten":
  // Zielgröße ≈ Videodauer × 0,22 MB/s (die fertigen 720p-Proxys des
  // Bestands liegen stabil bei diesem Wert, z. B. 127,9 MB / 592,5 s);
  // die Schreibrate kommt aus den letzten MB-Ständen des Polls. Beides
  // ehrlich als Schätzung gekennzeichnet, über 97 % gedeckelt.
  let proxyFortschritt = "";
  const zielMb = duration ? duration * 0.22 : null;
  const letzteMessung = proxyVerlauf[proxyVerlauf.length - 1];
  const ersteMessung = proxyVerlauf[0];
  if (letzteMessung && zielMb) {
    const prozent = Math.min(97, Math.round((letzteMessung.mb / zielMb) * 100));
    proxyFortschritt = ` ≈ ${prozent} %`;
    if (ersteMessung && letzteMessung.t > ersteMessung.t
        && letzteMessung.mb > ersteMessung.mb) {
      const rate = (letzteMessung.mb - ersteMessung.mb)
        / ((letzteMessung.t - ersteMessung.t) / 1000);
      const restMin = (zielMb - letzteMessung.mb) / rate / 60;
      proxyFortschritt += restMin < 1
        ? " · gleich fertig"
        : ` · noch ca. ${Math.ceil(restMin)} min`;
    }
    proxyFortschritt += " (Schätzung)";
  }

  const errorLine = error ? (
    wartetAufProxy ? (
      <div
        data-testid="coverage-proxy-wait"
        className="flex items-start gap-1.5 font-mono text-[10px] leading-snug text-cyan-200/90 whitespace-pre-wrap break-words"
      >
        <RefreshCw className="w-3 h-3 mt-[1px] shrink-0 animate-spin" />
        <span>
          {error.replace(/\s*Danach Seite neu laden\.?/i, "")}
          <b>{proxyFortschritt}</b>
          {" — aktualisiert sich selbst, die Abschnitte erscheinen automatisch."}
        </span>
      </div>
    ) : (
      <div
        data-testid="coverage-error"
        className="font-mono text-[10px] leading-snug text-rose-300 whitespace-pre-wrap break-words"
      >
        {error}
      </div>
    )
  ) : null;

  if (variant === "inline") {
    return (
      <div data-testid="camera-coverage-bar" className="space-y-1">
        {strip}
        {trackStrip}
        <div className="flex items-center gap-2.5 font-mono text-[9px] text-muted-foreground whitespace-nowrap overflow-hidden">
          <span title="Anker gesetzt">
            <span className="inline-block w-2 h-2 bg-emerald-500/80 rounded-[2px] mr-1 align-middle" />
            Anker
          </span>
          {trackStrip && (
            <span title="Kamera-Track (Optical Flow) vorhanden — teal Zeile darunter">
              <span className="inline-block w-2 h-2 bg-teal-400/80 rounded-[2px] mr-1 align-middle" />
              📹 Track
            </span>
          )}
          <span title="nur Segment-Label — Homographie-Anker fehlt">
            <span className="inline-block w-2 h-2 bg-amber-400/80 rounded-[2px] mr-1 align-middle" />
            nur Label
          </span>
          <span title="kein Anker — klicken zum Springen">
            <span className="inline-block w-2 h-2 border border-rose-400/70 rounded-[2px] mr-1 align-middle" />
            fehlt
          </span>
          <span title="In den dunklen Lücken schwenkt/zoomt die Kamera — dort ist kein stabiler Anker möglich">
            <span className="inline-block w-2 h-2 bg-secondary/70 rounded-[2px] mr-1 align-middle" />
            Lücke = Schwenk/Zoom
          </span>
          <span className="ml-auto flex items-center gap-2 normal-case shrink-0">
            {uncoveredLabel}
            {refreshBtn}
          </span>
        </div>
        {errorLine}
      </div>
    );
  }

  return (
    <div
      data-testid="camera-coverage-bar"
      className="border border-border bg-card rounded-sm p-3 space-y-2"
    >
      <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest">
        <span className="text-muted-foreground">Kamera-Abschnitte · Anker-Abdeckung</span>
        <span className="flex items-center gap-3 normal-case tracking-normal">
          {uncoveredLabel}
          {refreshBtn}
        </span>
      </div>
      {strip}
      {trackStrip}
      <div className="flex items-center gap-4 font-mono text-[9px] text-muted-foreground">
        {legend}
        <span className="ml-auto">Lücken = Kamera schwenkt/zoomt</span>
      </div>
      {errorLine}
    </div>
  );
};

export default CameraCoverageBar;
